import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({providedIn: 'root'})
export class FuseLoadingService
{
    private _auto$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private _mode$: BehaviorSubject<'determinate' | 'indeterminate'> = new BehaviorSubject<'determinate' | 'indeterminate'>('indeterminate');
    private _progress$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(0);
    private _show$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _urlMap: Map<string, boolean> = new Map<string, boolean>();

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for auto mode
     */
    get auto$(): Observable<boolean>
    {
        return this._auto$.asObservable();
    }

    /**
     * Getter for mode
     */
    get mode$(): Observable<'determinate' | 'indeterminate'>
    {
        return this._mode$.asObservable();
    }

    /**
     * Getter for progress
     */
    get progress$(): Observable<number>
    {
        return this._progress$.asObservable();
    }

    /**
     * Getter for show
     */
    get show$(): Observable<boolean>
    {
        return this._show$.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Show the loading bar
     */
    show(): void
    {
        // Defer to a new macrotask so this doesn't land in the middle of
        // whatever change-detection cycle triggered it (e.g. a dialog's
        // own ngOnInit firing an HTTP call), which would otherwise cause
        // ExpressionChangedAfterItHasBeenCheckedError in the loading bar.
        setTimeout(() => this._show$.next(true));
    }

    /**
     * Hide the loading bar
     */
    hide(): void
    {
        setTimeout(() => this._show$.next(false));
    }

    /**
     * Set the auto mode
     *
     * @param value
     */
    setAutoMode(value: boolean): void
    {
        this._auto$.next(value);
    }

    /**
     * Set the mode
     *
     * @param value
     */
    setMode(value: 'determinate' | 'indeterminate'): void
    {
        this._mode$.next(value);
    }

    /**
     * Set the progress of the bar manually
     *
     * @param value
     */
    setProgress(value: number): void
    {
        if ( value < 0 || value > 100 )
        {
            console.error('Progress value must be between 0 and 100!');
            return;
        }

        this._progress$.next(value);
    }

    /**
     * Sets the loading status for a given request.
     *
     * `key` must be unique PER REQUEST, not per URL — two concurrent calls to
     * the exact same endpoint (common on this app: several widgets hitting the
     * same list endpoint at once) would otherwise collide on the same Map key.
     * With a shared key, the first request to finish deletes that key and hides
     * the bar while the second is still in flight; the second's own completion
     * then finds the key already gone and never contributes to re-showing it
     * correctly, which is how the bar could end up stuck visible (or hidden
     * mid-load) — the interceptor used to pass `req.url` here.
     *
     * @param status
     * @param key
     */
    _setLoadingStatus(status: boolean, key: string): void
    {
        // Return if the key was not provided
        if ( !key )
        {
            console.error('The request key must be provided!');
            return;
        }

        if ( status === true )
        {
            this._urlMap.set(key, status);
            setTimeout(() => this._show$.next(true));
        }
        else if ( status === false && this._urlMap.has(key) )
        {
            this._urlMap.delete(key);
        }

        // Only set the status to 'false' if all outgoing requests are completed
        if ( this._urlMap.size === 0 )
        {
            setTimeout(() => this._show$.next(false));
        }
    }
}
